import React from 'react';
import { Controller } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Button from '@/components/Button';
import { classCountOptions, CohortFormProps, useCohortForm } from '../../hooks/useCohortForm';

export const CohortForm: React.FC<CohortFormProps> = ({ cohortsEmpty }) => {
  const {
    mission,
    assessmentPage,
    control,
    formState: { errors },
    year,
    fields,
    loading: loadingSubmit,
    handleSubmit,
    handleFormSubmit,
    yearGroupsResult,
    keyStage,
  } = useCohortForm(cohortsEmpty);
  // const yearGroup = yearGroupsResult?.yearGroups?.find((yearGroup) => yearGroup?.documentId === mission?.yearGroup?.documentId);
  // const keyStage = keyStagesResult?.keyStages?.find((keyStage) => keyStage?.documentId === mission?.keyStage?.documentId);

  return (
    <div className="mt-6">
      <div>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                {assessmentPage?.cohortSection?.inputs?.missionAssessment}
              </label>
              <div className="h-10 rounded-lg border border-gray-300 bg-gray-50 p-2">
                <span className="text-sh-xxs text-gray-900">{mission?.roleModel}</span>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                {assessmentPage?.cohortSection?.inputs?.keyStage}
              </label>
              <div className="h-10 rounded-lg border border-gray-300 bg-gray-50 p-2">
                <span className="text-sh-xxs text-gray-900">{keyStage?.keyStage}</span>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                {assessmentPage?.cohortSection?.inputs?.schoolYear}
              </label>
              <div className="h-10 rounded-lg border border-gray-300 bg-gray-50 p-2">
                <span className="text-sh-xxs text-gray-900">{year}</span>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">{'Year group'}</label>
              <Controller
                control={control}
                name="yearGroupDocumentId"
                render={({ field: { onChange, value } }) => (
                  <Select onValueChange={onChange} value={value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select year group" />
                    </SelectTrigger>
                    <SelectContent>
                      {yearGroupsResult?.yearGroups?.map((yearGroup) => (
                        <SelectItem key={yearGroup?.documentId} value={yearGroup?.documentId || ''}>
                          {yearGroup?.yearGroup}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.yearGroupDocumentId && (
                <p className="mt-1 text-sm text-red-600">{errors.yearGroupDocumentId.message}</p>
              )}
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                {assessmentPage?.cohortSection?.inputs?.howManyClasses}
              </label>
              <Controller
                control={control}
                name="howManyClasses"
                render={({ field: { onChange, value } }) => (
                  <Select onValueChange={(val) => onChange(parseInt(val, 10))} value={value?.toString()}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select number of classes" />
                    </SelectTrigger>
                    <SelectContent>
                      {classCountOptions.map((option) => (
                        <SelectItem key={option.label} value={option.label.toString()}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.howManyClasses && <p className="mt-1 text-sm text-red-600">{errors.howManyClasses.message}</p>}
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                {assessmentPage?.cohortSection?.inputs?.classNames}
              </label>
              <div className="space-y-3">
                {fields.map((field, index) => (
                  <div key={field.id} className="flex gap-3">
                    <div className="flex-1">
                      <Controller
                        control={control}
                        name={`classNames.${index}.name`}
                        render={({ field: { onChange, value, onBlur } }) => (
                          <Input
                            placeholder={assessmentPage?.cohortSection?.inputs?.classNamePlaceholder}
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            onBlur={onBlur}
                          />
                        )}
                      />
                      {errors.classNames?.[index]?.name && (
                        <p className="mt-1 text-sm text-red-600">{errors.classNames[index]?.name?.message}</p>
                      )}
                    </div>
                  </div>
                ))}
                {errors.classNames && typeof errors.classNames.message === 'string' && (
                  <p className="mt-1 text-sm text-red-600">{errors.classNames.message}</p>
                )}
              </div>
            </div>

            <div>
              <Controller
                control={control}
                name="cohortName"
                render={({ field: { onChange, value, onBlur } }) => (
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      {assessmentPage?.cohortSection?.inputs?.cohortName}
                    </label>
                    <Input value={value} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} />
                  </div>
                )}
              />
              {errors.cohortName && <p className="mt-1 text-sm text-red-600">{errors.cohortName.message}</p>}
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="submit" theme="solid" loading={loadingSubmit}>
              {assessmentPage?.cohortSection?.inputs?.submitButton}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
